import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Flex, Form, Radio, Select, Skeleton, Tag, Typography, Upload } from 'antd';
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import { Utils } from '../../services/helpers/Utils';
import useApiClient from '../../hooks/api/useApiClient';
import useForm from '../../hooks/utility/useForm';
import { useGetBusinessDocumentsQuery, useSubmitBusinessDocumentMutation } from '../../store/api-slices';
import useHelpers from '../../hooks/context/useHelpers';

export default function DocumentVerificationStep(props) {
  const {
    keyPrefix,
    documentType,
    documentTypeOptions = [],
    expiryRequired = true,
    moveBackwards,
    moveForward,
  } = props;

  const { t } = useTranslation(undefined, { keyPrefix });
  const { dateInputFormat, formatDate } = useHelpers();

  const { BusinessProfilesManager } = useApiClient();

  const { data: documentsResponse, isLoading: loadingDocuments } = useGetBusinessDocumentsQuery({
    apiManager: BusinessProfilesManager,
    params: {
      'filter[type]': documentType,
    },
  });
  const [submitDocument, { isLoading: saving }] = useSubmitBusinessDocumentMutation();

  const documentGroups = useMemo(() => {
    const documents = documentsResponse ?? [];
    const groups = {};

    documents.forEach((document) => {
      groups[document.type] ||= { type: document.type, kind: document.kind };
      groups[document.type][document.side] = document;
    });

    return Object.values(groups);
  }, [documentsResponse]);

  const { data, update } = useForm({
    document_type: null,
    front: null,
    back: null,
    expires_at: null,
  });

  const [selectedType, setSelectedType] = useState(null);
  const [uploadNew, setUploadNew] = useState(false);
  const { setFeedback, renderFeedbackLabel } = useFeedbackLabel();

  const hasExistingDocuments = documentGroups.length > 0;
  const showUploadForm = uploadNew || !hasExistingDocuments;

  async function save(mutation, payload) {
    const { error } = await mutation({ apiManager: BusinessProfilesManager, data: payload });

    if (error) {
      return setFeedback({
        message: error?.message,
        type: 'error',
        errors: error?.errors,
      });
    }

    return moveForward();
  }

  const sendExistingDocument = () => {
    return save(submitDocument, {
      type: documentType,
    });
  };

  const uploadNewDocument = () =>
    save(
      submitDocument,
      Utils.createFormData({
        type: documentType,
        kind: data.document_type,
        expires_at: data.expires_at,
        front: data.front,
        back: data.back ?? null,
      }),
    );

  const renderExpiresAtField = () => (
    <Form.Item label={t('form.expires_at.label')} required={expiryRequired}>
      <DatePicker
        format={[dateInputFormat, 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']}
        value={data.expires_at ? dayjs(data.expires_at) : null}
        placeholder={t('form.expires_at.placeholder')}
        disabledDate={(current) => current && current < dayjs().startOf('day')}
        onChange={(value) => update('expires_at', value ? value.utc(true).toISOString() : null)}
      />
    </Form.Item>
  );

  const renderDocumentDates = (group) => {
    const document = group.front ?? group.back;

    if (!document) {
      return null;
    }

    return (
      <Typography.Text type="secondary" className="text-xs">
        {document.created_at && t('existing_documents.created_at', { date: formatDate(document.created_at) })}
        {document.created_at && document.expires_at && ' · '}
        {document.expires_at && t('existing_documents.expires_at', { date: formatDate(document.expires_at) })}
      </Typography.Text>
    );
  };

  const renderExistingDocuments = () => (
    <>
      <Typography.Paragraph>{t('existing_documents.description')}</Typography.Paragraph>
      <Radio.Group value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="w-full mb-lg">
        <Flex vertical gap={8}>
          {documentGroups.map((group) => (
            <Radio key={group.type} value={group.type} className="border border-solid border-gray-200 rounded-lg p-3">
              <Flex vertical gap={4}>
                <Flex align="center" gap={8} wrap>
                  <FileTextOutlined />
                  <span>{t(`form.document_type.options.${group.kind}`, { defaultValue: group.kind })}</span>
                  {group.front && <Tag color="blue">{t('existing_documents.sides.front')}</Tag>}
                  {group.back && <Tag color="blue">{t('existing_documents.sides.back')}</Tag>}
                </Flex>
                {renderDocumentDates(group)}
              </Flex>
            </Radio>
          ))}
        </Flex>
      </Radio.Group>
      <Flex justify="end" wrap gap={8}>
        <Button onClick={moveBackwards}>{t('buttons.back')}</Button>
        <Button onClick={() => setUploadNew(true)}>{t('buttons.upload_new')}</Button>
        <Button type="primary" onClick={sendExistingDocument} loading={saving} disabled={!selectedType}>
          {t('buttons.send')}
        </Button>
      </Flex>
    </>
  );

  const renderUploadForm = () => (
    <>
      <Form layout="vertical">
        <Form.Item label={t('form.document_type.label')} required>
          <Select
            placeholder={t('form.document_type.placeholder')}
            value={data.document_type}
            onChange={(value) => update('document_type', value)}
            options={documentTypeOptions.map((option) => ({
              label: t(`form.document_type.options.${option}`),
              value: option,
            }))}
          />
        </Form.Item>
        <Form.Item label={t('form.front.label')} required>
          <Upload
            listType="picture"
            multiple={false}
            maxCount={1}
            accept="image/png,image/jpeg,application/pdf"
            beforeUpload={(file) => {
              update('front', file);

              return false;
            }}
            onRemove={() => update('front', null)}
          >
            <Button icon={<UploadOutlined />}>{t('buttons.select_file')}</Button>
          </Upload>
        </Form.Item>
        <Form.Item label={t('form.back.label')}>
          <Upload
            listType="picture"
            multiple={false}
            maxCount={1}
            accept="image/png,image/jpeg,application/pdf"
            beforeUpload={(file) => {
              update('back', file);

              return false;
            }}
            onRemove={() => update('back', null)}
          >
            <Button icon={<UploadOutlined />}>{t('buttons.select_file')}</Button>
          </Upload>
        </Form.Item>
        {renderExpiresAtField()}
      </Form>
      <Flex justify="end" wrap gap={8}>
        <Button onClick={moveBackwards}>{t('buttons.back')}</Button>
        {hasExistingDocuments && <Button onClick={() => setUploadNew(false)}>{t('buttons.use_existing')}</Button>}
        <Button
          type="primary"
          onClick={uploadNewDocument}
          loading={saving}
          disabled={!data.front || (expiryRequired && !data.expires_at)}
        >
          {t('buttons.next')}
        </Button>
      </Flex>
    </>
  );

  return (
    <FadeIn>
      {renderFeedbackLabel}
      {loadingDocuments ? <Skeleton active /> : showUploadForm ? renderUploadForm() : renderExistingDocuments()}
    </FadeIn>
  );
}
